import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { getTeamScores, getTeamTrend, type TrendPoint } from '../api/team';
import { getMyHistory, type Assessment } from '../api/assessment';
import { useAuth } from '../store/AuthContext';
import { exportToCsv, todayIso } from '../utils/exportCsv';

const dimensionOrder = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6'];
const dimensionLabel: Record<string, string> = {
  d1: 'Tooling',
  d2: 'Prompts',
  d3: 'Workflow',
  d4: 'Skill',
  d5: 'Measurement',
  d6: 'Culture',
};

export function ManagerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [team, setTeam] = useState<Assessment[] | null>(null);
  const [trend, setTrend] = useState<TrendPoint[] | null>(null);
  const [mine, setMine] = useState<Assessment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getTeamScores(), getTeamTrend(), getMyHistory()])
      .then(([t, tr, h]) => {
        setTeam(t.assessments);
        setTrend(tr.trend);
        setMine(h.assessments);
      })
      .catch((err: unknown) => {
        const m = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(m ?? 'Failed to load manager view');
      });
  }, []);

  const teamAverages = useMemo(() => {
    if (!team || team.length === 0) return null;
    const dims: Record<string, number[]> = { d1: [], d2: [], d3: [], d4: [], d5: [], d6: [] };
    let total = 0;
    for (const a of team) {
      total += a.totalScore;
      for (const d of dimensionOrder) {
        dims[d].push(a.dimensionScores?.[d] ?? 0);
      }
    }
    const avg = (xs: number[]) => xs.reduce((s, v) => s + v, 0) / Math.max(xs.length, 1);
    return {
      total: total / team.length,
      dims: Object.fromEntries(dimensionOrder.map((d) => [d, avg(dims[d])])) as Record<string, number>,
    };
  }, [team]);

  const trendByPeriod = useMemo(() => {
    if (!trend) return [] as Array<{ period: string; avg: number; n: number }>;
    const grouped = new Map<string, number[]>();
    for (const p of trend) {
      const arr = grouped.get(p.cadencePeriod) ?? [];
      arr.push(p.totalScore);
      grouped.set(p.cadencePeriod, arr);
    }
    return Array.from(grouped.entries())
      .map(([period, scores]) => ({
        period,
        avg: scores.reduce((s, v) => s + v, 0) / scores.length,
        n: scores.length,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [trend]);

  if (!team || !trend || !mine) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4" component="h1">Manager View</Typography>
        <Typography variant="body2">
          Team scores for {user?.email}. Self-eval scores are excluded automatically.
        </Typography>
      </Box>

      {error && <Alert severity="error" role="alert">{error}</Alert>}

      <Paper elevation={5}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="TEAM SCORES" sx={{ minWidth: 72, maxWidth: 264, height: 48, textTransform: 'uppercase' }} />
          <Tab label="TREND" sx={{ minWidth: 72, maxWidth: 264, height: 48, textTransform: 'uppercase' }} />
          <Tab label="MY HISTORY" sx={{ minWidth: 72, maxWidth: 264, height: 48, textTransform: 'uppercase' }} />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Card elevation={3}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" component="h2">
                Latest Team Submissions
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={team.length === 0}
                onClick={() => {
                  if (team.length === 0) return;
                  const rows = team.map((a) => ({
                    email: a.userEmail ?? a.userId,
                    period: a.cadencePeriod,
                    submitted: new Date(a.submittedAt).toISOString(),
                    totalScore: a.totalScore.toFixed(2),
                    maturityLevel: a.maturityLevel,
                    d1: (a.dimensionScores?.d1 ?? 0).toFixed(2),
                    d2: (a.dimensionScores?.d2 ?? 0).toFixed(2),
                    d3: (a.dimensionScores?.d3 ?? 0).toFixed(2),
                    d4: (a.dimensionScores?.d4 ?? 0).toFixed(2),
                    d5: (a.dimensionScores?.d5 ?? 0).toFixed(2),
                    d6: (a.dimensionScores?.d6 ?? 0).toFixed(2),
                  }));
                  exportToCsv(`ai-maturity-team-${user?.teamId ?? 'team'}-${todayIso()}.csv`, rows);
                }}
              >
                {team.length === 0 ? 'No data' : 'Export CSV'}
              </Button>
            </Box>
            {teamAverages && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Team average (n={team.length}): <strong>{teamAverages.total.toFixed(2)}</strong>
              </Typography>
            )}
            {team.length === 0 ? (
              <Typography variant="body2">No assessments yet.</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Level</TableCell>
                      {dimensionOrder.map((d) => (
                        <TableCell key={d} align="right">{dimensionLabel[d]}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {team.map((a) => (
                      <TableRow key={a.assessmentId}>
                        <TableCell>{a.userEmail ?? `${a.userId.slice(0, 8)}...`}</TableCell>
                        <TableCell>{a.cadencePeriod}</TableCell>
                        <TableCell>{new Date(a.submittedAt).toLocaleString()}</TableCell>
                        <TableCell align="right">{a.totalScore.toFixed(2)}</TableCell>
                        <TableCell align="right">L{a.maturityLevel}</TableCell>
                        {dimensionOrder.map((d) => (
                          <TableCell key={d} align="right">
                            {(a.dimensionScores?.[d] ?? 0).toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              Team Trend
            </Typography>
            {trendByPeriod.length === 0 ? (
              <Typography variant="body2">No trend data yet.</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Submissions</TableCell>
                      <TableCell align="right">Average Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trendByPeriod.map((p) => (
                      <TableRow key={p.period}>
                        <TableCell>{p.period}</TableCell>
                        <TableCell align="right">{p.n}</TableCell>
                        <TableCell align="right">{p.avg.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              My History
            </Typography>
            {mine.length === 0 ? (
              <Typography variant="body2">You haven't taken an assessment yet.</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Level</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mine.map((a) => (
                      <TableRow key={a.assessmentId}>
                        <TableCell>{a.cadencePeriod}</TableCell>
                        <TableCell>{new Date(a.submittedAt).toLocaleString()}</TableCell>
                        <TableCell align="right">{a.totalScore.toFixed(2)}</TableCell>
                        <TableCell align="right">L{a.maturityLevel}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
